import React, { useState, useEffect } from 'react';
import { Select, Typography, Space } from 'antd';
import { Store } from 'lucide-react';
import apiService from '../../services/api.service';

const { Text } = Typography;

const ACTIVE_TENANT_KEY = 'upos_active_tenant_id';
const ACTIVE_TENANT_NAME_KEY = 'upos_active_tenant_name';

export function getActiveTenantId() {
    return localStorage.getItem(ACTIVE_TENANT_KEY);
}

export function getActiveTenantName() {
    return localStorage.getItem(ACTIVE_TENANT_NAME_KEY);
}

export function setActiveTenant(id, name) {
    if (id) {
        localStorage.setItem(ACTIVE_TENANT_KEY, String(id));
        if (name) localStorage.setItem(ACTIVE_TENANT_NAME_KEY, name);
    } else {
        localStorage.removeItem(ACTIVE_TENANT_KEY);
        localStorage.removeItem(ACTIVE_TENANT_NAME_KEY);
    }
    window.dispatchEvent(new CustomEvent('upos:tenant-changed', { detail: { id, name } }));
}

const TenantSwitcher = ({ user, compact = false }) => {
    const [tenants, setTenants] = useState([]);
    const [activeId, setActiveId] = useState(
        getActiveTenantId() || String(user?.homeTenantId || user?.tenantId || '')
    );
    const [loading, setLoading] = useState(false);

    const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'super-admin';

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                if (isSuperAdmin) {
                    const result = await apiService.tenant.getAll();
                    if (result.success) {
                        setTenants((result.tenants || []).map((t) => ({
                            id: t.id,
                            business_name: t.business_name,
                            status: t.status,
                        })));
                    }
                } else {
                    const result = await apiService.userCompany.getMyCompanies();
                    if (result.success && result.companies?.length) {
                        setTenants(result.companies.map((c) => ({
                            id: c.tenant_id,
                            business_name: c.business_name,
                            status: c.is_active ? 'active' : 'inactive',
                        })));
                    } else {
                        const tid = String(user?.tenantId || '');
                        if (tid) {
                            setTenants([{ id: tid, business_name: user?.businessName, status: 'active' }]);
                        }
                    }
                }

                const stored = getActiveTenantId();
                if (stored) {
                    setActiveId(stored);
                } else if (user?.tenantId) {
                    setActiveId(String(user.tenantId));
                    setActiveTenant(user.tenantId, user.businessName);
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isSuperAdmin, user?.tenantId, user?.businessName]);

    const handleChange = (value) => {
        const tenant = tenants.find((t) => String(t.id) === String(value));
        setActiveId(String(value));
        setActiveTenant(value, tenant?.business_name);
        window.location.reload();
    };

    const multiCompany = tenants.length > 1;

    if (!multiCompany && !isSuperAdmin) {
        if (compact) return null;
        return (
            <Space size={4}>
                <Store size={16} className="text-muted" />
                <Text className="text-muted" style={{ fontSize: 12 }}>{user?.businessName || 'My Business'}</Text>
            </Space>
        );
    }

    return (
        <Select
            size="small"
            value={activeId || undefined}
            onChange={handleChange}
            loading={loading}
            style={{ minWidth: compact ? 160 : 200 }}
            placeholder="Select business"
            options={tenants.map((t) => ({
                value: String(t.id),
                label: `${t.business_name}${t.status === 'inactive' ? ' (inactive)' : ''}`,
                disabled: t.status === 'inactive',
            }))}
        />
    );
};

export default TenantSwitcher;
